import React, { useCallback, useEffect, useState } from 'react';
import { Grid, GridCol, GridRow } from '@naf/grid';
import { breakpoints, spacing, radius } from '@naf/theme';
import { themeLight } from '@lib/nafTheme';
import { Text, TextVariant } from '@naf/text';
import { nafColor } from '@nafcore/theme';
import styled from '@emotion/styled';
import { AdvancedImage } from '@cloudinary/react';
import { useWindowResize } from '@hooks/Window/useWindowResize';
import { useCloudinary } from '@hooks/Cloudinary/useCloudinary';
import { InputWithImageLinkType } from '@appTypes/inputWithImageLinkType';
import { InputBlock } from './InputBlock';

type InputWithImageLinkBlockProps = {
  block: InputWithImageLinkType;
};

export const InputWithImageLinkCard = ({
  item: { title, link, image, label, validation, key },
}: {
  item: InputWithImageLinkType;
}) => {
  const publicId = image?.public_id || image?.publicId;
  const { getResponsiveImage } = useCloudinary();
  const [cldImage, setCldImage] = useState(getResponsiveImage(publicId, { width: 1120 }));

  const advancedImage = cldImage ? <AdvancedImage alt={image?.alt || image?.altText} cldImg={cldImage} /> : undefined;

  const setResponsiveImage = useCallback(() => {
    if (typeof window !== 'undefined') {
      if (image && publicId) {
        if (window.innerWidth <= parseInt(breakpoints.m, 10)) {
          const width = window.innerWidth;
          const height = window.innerWidth;
          setCldImage(getResponsiveImage(publicId, { width, height }));
        } else {
          // If screen is large, use max 1120 (container) as width
          const width = 1120;
          setCldImage(getResponsiveImage(publicId, { width }));
        }
      }
    }
  }, [getResponsiveImage, image, publicId]);

  useEffect(() => {
    setResponsiveImage();
  }, [setResponsiveImage]);

  useWindowResize(() => {
    setResponsiveImage();
  });

  return (
    <BlockWrapper>
      <ImageWrapper>{advancedImage}</ImageWrapper>
      <TextWrapper>
        {title && (
          <HeaderText tag="h2" variant={TextVariant.Header2}>
            {title}
          </HeaderText>
        )}
        <InputBlock item={{ link, label, validation, key }} />
      </TextWrapper>
    </BlockWrapper>
  );
};

export const InputWithImageLinkBlock = ({ block }: InputWithImageLinkBlockProps) => (
  <Grid className="InputWithImageLinksBlock">
    <GridRow>
      <GridCol s="12" m="12" l="12" xl="12" key={block.key}>
        <InputWithImageLinkCard item={block} />
      </GridCol>
    </GridRow>
  </Grid>
);

const ImageWrapper = styled.figure`
  position: relative;
  margin: 0;
  border-radius: ${radius.m};
  max-height: 600px;
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5));
    border-radius: ${radius.m};

    @media (max-width: ${breakpoints.s}) {
      display: none;
    }
  }
  img {
    border-radius: ${radius.m};
    width: 100%;
    display: block;
    max-height: 600px;
    object-fit: cover;
    @media (max-width: ${breakpoints.s}) {
      border-radius: 0;
    }
  }
  @media (max-width: ${breakpoints.s}) {
    border-radius: 0;
  }
`;

const HeaderText = styled(Text)`
  color: ${nafColor.signature.white || 'FFF'};

  @media (max-width: ${breakpoints.s}) {
    margin-top: 0;
    padding-top: ${spacing.space8};
  }
`;

const BlockWrapper = styled.div`
  position: relative;
  @media (max-width: ${breakpoints.s}) {
    margin: 0 -${spacing.space24};
  }
`;

const TextWrapper = styled.div`
  position: absolute;
  top: calc(50% - (156px / 2));
  left: ${spacing.space80};
  @media (max-width: ${breakpoints.m}) {
    min-width: 410px;
    left: 50%;
    -ms-transform: translateX(-50%);
    transform: translateX(-50%);
  }
  @media (max-width: ${breakpoints.s}) {
    position: static;
    transform: none;
    min-width: 0;
    padding: ${spacing.space24};
    background: ${({ theme }) =>
      theme.background ? theme.background.brandInverted : themeLight.background.brandInverted};
  }
`;
